'use client';

import React, { useRef, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';

const testimonials = [
  {
    id: 't1',
    quote: 'Judith turned my 20-year career into a concise, high-impact business case. The free master templates and prompts also saved me thousands since I can tailor future applications myself without paying another dime.',
    name: 'Marcus T.',
    role: 'Vice President of Engineering',
    industry: 'Enterprise Software',
    stars: 5,
    tier: 'Career Brand Suite',
  },
  {
    id: 't2',
    quote: 'Working with Judith was completely seamless and razor-sharp. Her ability to translate complex healthcare operations and P&L metrics into ATS-friendly narrative gave me immediate traction with executive search recruiters.',
    name: 'Elena R.',
    role: 'Chief Operating Officer',
    industry: 'Healthcare & Medical Devices',
    stars: 5,
    tier: 'Career Brand Suite',
  },
  {
    id: 't3',
    quote: 'The executive bio and LinkedIn overhaul alone were worth every penny. Decision-makers specifically referenced how cohesive my entire personal brand looked across touchpoints. Highly recommended!',
    name: 'David K.',
    role: 'Managing Director',
    industry: 'Private Equity & Asset Management',
    stars: 5,
    tier: 'Career Brand Suite',
  },
  {
    id: 't4',
    quote: 'I was stuck applying without responses for months. Judith reworked my entire application suite to highlight quantifiable impact. I received my first interview invitation within four days of re-applying.',
    name: 'Sarah M.',
    role: 'Senior Director of Global Supply Chain',
    industry: 'Manufacturing',
    stars: 5,
    tier: 'Complete Targeted Application Suite',
  },
];

const CriticalPraise: React.FC = () => {
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add('active');
        });
      },
      { threshold: 0.08 }
    );
    const els = sectionRef.current?.querySelectorAll('.reveal, .reveal-left');
    els?.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} id="testimonials" className="py-28 px-6 bg-obsidian relative overflow-hidden">
      {/* Large background text */}
      <div
        className="absolute inset-0 flex items-center justify-center pointer-events-none select-none overflow-hidden"
        aria-hidden="true"
      >
        <span
          className="font-display font-light text-parchment/[0.025] whitespace-nowrap"
          style={{ fontSize: 'clamp(8rem, 20vw, 18rem)', lineHeight: 1 }}
        >
          Results
        </span>
      </div>

      <div className="max-w-6xl mx-auto relative z-10">
        {/* Header */}
        <div className="text-center mb-20 reveal">
          <span className="text-xs font-body font-semibold text-gold tracking-[0.3em] uppercase mb-4 block">Client Outcomes</span>
          <h2 className="font-display font-light text-parchment leading-tight" style={{ fontSize: 'clamp(2.5rem, 5vw, 4rem)' }}>
            What executives say<br />
            <span className="italic text-parchment/50">after working with Judith.</span>
          </h2>
        </div>

        {/* Testimonials */}
        <div className="space-y-0 divide-y divide-parchment/8">
          {testimonials.map((item, idx) => (
            <div
              key={item.id}
              className="reveal py-12 grid grid-cols-1 lg:grid-cols-12 gap-8 items-start group"
              style={{ transitionDelay: `${idx * 100}ms` }}
            >
              {/* Number */}
              <div className="lg:col-span-1 text-parchment/15 font-display text-5xl font-light leading-none">
                {String(idx + 1).padStart(2, '0')}
              </div>

              {/* Quote */}
              <div className="lg:col-span-8">
                <blockquote className="font-display font-light italic text-parchment text-xl md:text-2xl leading-relaxed mb-6 group-hover:text-gold/90 transition-colors duration-500">
                  "{item.quote}"
                </blockquote>
                <div className="flex items-center gap-3">
                  <div className="flex gap-0.5">
                    {Array.from({ length: item.stars }).map((_, i) => (
                      <Icon key={i} name="StarIcon" size={12} className="text-gold" variant="solid" />
                    ))}
                  </div>
                  <span className="text-parchment/30 text-xs">·</span>
                  <span className="text-parchment/60 text-sm font-body font-semibold">{item.name}</span>
                  <span className="text-parchment/30 text-xs">·</span>
                  <span className="text-parchment/40 text-sm font-body">{item.role}</span>
                </div>
              </div>

              {/* Industry + Tier */}
              <div className="lg:col-span-3 text-right">
                <p className="font-body font-semibold text-gold text-xs tracking-widest uppercase mb-1">{item.industry}</p>
                <p className="font-display italic text-parchment/30 text-sm">{item.tier}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Strategic pillars */}
        <div className="mt-20 pt-12 border-t border-parchment/8 reveal">
          <p className="text-xs font-body font-semibold text-parchment/30 tracking-[0.3em] uppercase text-center mb-8">Core Strategic Pillars in Every Engagement</p>
          <div className="flex flex-wrap items-center justify-center gap-8 lg:gap-12">
            {[
              'ATS Search Indexing',
              'Quantifiable Impact',
              'Leadership Presence',
              'Executive Design',
            ].map((pillar) => (
              <div key={pillar} className="flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-gold/50" />
                <span className="font-display italic text-parchment/50 text-sm">{pillar}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default CriticalPraise;